import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useContactSearch } from '@/hooks/useContactSearch';

export interface ContactOption {
  id: string;
  first_name?: string;
  last_name?: string;
  primary_email?: string;
  primary_phone?: string;
}

interface ContactSearchComboboxProps {
  value: string;
  onValueChange: (contactId: string, contact?: ContactOption) => void;
  placeholder?: string;
  excludeContactId?: string;
  selectedContact?: ContactOption;
}

export function ContactSearchCombobox({
  value,
  onValueChange,
  placeholder = 'Buscar contato...',
  excludeContactId,
  selectedContact: propSelectedContact,
}: ContactSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { contacts, loading } = useContactSearch(searchQuery, 50);

  // Filter out excluded contact
  const filteredContacts = excludeContactId
    ? contacts.filter((c) => c.id !== excludeContactId)
    : contacts;

  // Use prop selectedContact if provided, otherwise search in contacts list
  const foundContact = contacts.find((c) => c.id === value);
  const displayContact = foundContact || propSelectedContact;
  const displayName = displayContact
    ? `${displayContact.first_name || ''} ${displayContact.last_name || ''}`.trim() || 'Sem nome'
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate">{displayName}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Digite o nome do contato..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty>
              {loading 
                ? 'Buscando...' 
                : searchQuery.length < 1 
                  ? 'Digite para buscar...' 
                  : 'Nenhum contato encontrado.'}
            </CommandEmpty>
            <CommandGroup>
              {filteredContacts.map((contact) => {
                const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Sem nome';
                const subtitle = contact.primary_email || contact.primary_phone || '';

                  return (
                    <CommandItem
                      key={contact.id}
                      value={contact.id}
                      onSelect={(currentValue) => {
                        const selectedContact = filteredContacts.find(c => c.id === currentValue);
                        onValueChange(currentValue === value ? '' : currentValue, selectedContact);
                        setOpen(false);
                      }}
                    >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-primary">
                          {contact.first_name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="font-medium truncate">{name}</span>
                        {subtitle && (
                          <span className="text-xs text-muted-foreground truncate">
                            {subtitle}
                          </span>
                        )}
                      </div>
                    </div>
                    <Check
                      className={cn(
                        'ml-2 h-4 w-4 shrink-0',
                        value === contact.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
